import {
  type Product,
  type ProductType,
  productTypeToSuperCategoryMap,
  MAXIMUM_ALTERNATIVE_PRODUCTS,
  type ProductTechnology,
  type ProductBundle,
  productIsLazy,
  productBundleDataIsResolved,
  type BundleContentsProduct,
  productExistsOnPlp
} from '@/domain/Product';
import * as ECAPI from './EcommerceService.ts';
import {
  createProductMaps,
  fromEcapiDto,
  type CustomerProductRules
} from '@/domain/assemblers/ProductAssembler';
import * as SubscriptionService from './SubscriptionService.ts';
import * as PromotionsService from './PromotionsService.ts';
import * as ContentService from './ContentService.ts';
import * as PricingService from './PricingService.ts';
import { getAlternativeProducts } from '@/domain/Alternatives.ts';
import asyncFailToDefault from '@/utils/asyncFailToDefault.ts';
import type { SpecificProductsResponseDTO } from '@/services/generated/ECAPICustomersDTOs.ts';
import createI18nInstance from '@/utils/createI18nInstance.ts';
import { type UserGroupsDTO } from './EcommerceService.ts';
import {
  defaultProductTranslations,
  type ProductTranslations
} from '@/domain/config/index.ts';
import {
  getAssociatedBundlesByProductId,
  mapAssociatedBundles
} from '@/domain/assemblers/BundleAssembler.ts';

export async function getProduct(
  market: string,
  channel: string,
  productType: ProductType,
  locale: string,
  slug: string,
  cacheBurst?: boolean
): Promise<Product | undefined> {
  const products = await getProductsList(
    market,
    channel,
    productType,
    locale,
    undefined,
    cacheBurst
  );

  const product = products.find((prod) => prod.slug === slug);

  if (productType === 'machine' && product) {
    try {
      const { ratingAverage, reviewCount } = await ECAPI.ratingSummary(
        market,
        locale,
        product.id
      );
      product.rating = { ratingAverage, reviewCount };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Fetching Product Ratings Failed: ', (e as Error).message);
    }
  }

  return product;
}

export async function getProducts(
  market: string,
  channel: string,
  productType: ProductType,
  locale: string,
  technology?: ProductTechnology,
  customerProductRules?: CustomerProductRules,
  cacheBurst?: boolean
): Promise<Product[]> {
  const productsList = await getProductsList(
    market,
    channel,
    productType,
    locale,
    customerProductRules,
    cacheBurst
  );

  if (technology === undefined) {
    return productsList;
  }

  const products = productsList.filter(({ type, technologies }) =>
    productExistsOnPlp(type, technologies, technology)
  );

  // TODO: Resolve ratings
  // if (productType === 'machine' && product) {
  //   try {
  //     const { ratingAverage, reviewCount } = await window.napi
  //       .ratings()
  //       .summary(product.id);
  //     product.rating = { ratingAverage, reviewCount };
  //   } catch (e) {
  //     // eslint-disable-next-line no-console
  //     console.warn('Fetching Product Ratings Failed: ', (e as Error).message);
  //   }
  // }

  return products;
}

export async function getCustomerProductRules(
  market: string,
  channel: string,
  locale: string,
  cacheBurst?: boolean
): Promise<CustomerProductRules> {
  const [customerProductBTGRules, customerSelections] = await Promise.all([
    asyncFailToDefault<SpecificProductsResponseDTO | undefined>(
      ECAPI.customerSpecificProducts(market, channel, cacheBurst),
      undefined // User is not logged in
    ),
    asyncFailToDefault<UserGroupsDTO | undefined>(
      ECAPI.customerSelections(market, locale, cacheBurst),
      undefined // User is not logged in
    )
  ]);

  return {
    selections: customerProductBTGRules?.selections,
    segments: customerSelections?.userGroups
  };
}

async function getProductsList(
  market: string,
  channel: string,
  productType: ProductType,
  locale: string,
  customerProductRules?: CustomerProductRules,
  cacheBurst?: boolean
): Promise<Product[]> {
  // For ANONYMOUS the response has default BTG rules, for NONE we need to apply the user specific rules
  const customerState = customerProductRules?.selections ? 'NONE' : 'ANONYMOUS';

  const [
    productsByCategories,
    pricingMap,
    subscriptions,
    promotions,
    enrichments,
    productCategories,
    stocksArray
  ] = await Promise.all([
    ECAPI.productsByCategories(
      market,
      channel,
      locale,
      productTypeToSuperCategoryMap[productType],
      customerState,
      cacheBurst
    ),
    PricingService.getPrices(market, channel, cacheBurst),
    asyncFailToDefault(
      SubscriptionService.getSubscriptions(market, channel, locale, cacheBurst),
      []
    ),
    asyncFailToDefault(
      PromotionsService.getPromotions(market, channel, locale, cacheBurst),
      []
    ),
    ContentService.getProductEnrichmentsDefinition(
      market,
      channel,
      locale,
      cacheBurst
    ),
    ECAPI.productCategories(market, channel, locale, cacheBurst),
    ECAPI.stocks(market, channel, cacheBurst)
  ]);

  const [productDtoMap, rangesMap, modelsToRanges, metadataMap] =
    createProductMaps(
      productsByCategories,
      productCategories,
      customerProductRules
    );

  const stocks = new Map<string, boolean>(
    stocksArray.map((item) => [item.productId, item.isInStock])
  );

  const productDtos = [...productDtoMap.values()].filter(
    ({ id }) => pricingMap.has(id) && (pricingMap.get(id)?.price ?? 0) >= 0
  );

  const productI18nInstance = createI18nInstance<ProductTranslations>(
    defaultProductTranslations,
    enrichments.translations,
    locale
  );

  const products = productDtos.map((productDto) =>
    fromEcapiDto(
      productDto,
      pricingMap,
      subscriptions,
      rangesMap,
      promotions,
      enrichments,
      productCategories,
      modelsToRanges,
      stocks,
      productI18nInstance,
      undefined,
      productDtoMap
    )
  );

  const associatedBundlesMap = mapAssociatedBundles(
    enrichments.enrichments?.products || {}
  );

  for (const prod of products) {
    if (!prod.inStock) {
      prod.alternativeProducts = getAlternativeProducts(prod, products)
        .slice(0, MAXIMUM_ALTERNATIVE_PRODUCTS)
        .map(({ product: altProd }) => altProd);
    }

    prod.metadata = metadataMap.get(prod.id);

    prod.associatedBundles = getAssociatedBundlesByProductId(
      prod.id,
      associatedBundlesMap,
      products
    );
  }

  return products;
}

// @todo - add fixtures to test this
/* v8 ignore start */
export async function getProductById(
  id: string,
  market: string,
  channel: string,
  locale: string,
  cacheBurst?: boolean
): Promise<Product | null> {
  // fetch product first because we need to know the product.type
  const productByIdDto = await ECAPI.productById(market, channel, locale, id);
  const realProductType = productByIdDto.type as ProductType | undefined;

  if (realProductType === undefined) {
    // couldn't resolve product by ID
    return null;
  }

  const [
    productsByCategories,
    pricingMap,
    subscriptions,
    promotions,
    enrichments,
    productCategories,
    stocksArray
  ] = await Promise.all([
    ECAPI.productsByCategories(
      market,
      channel,
      locale,
      productTypeToSuperCategoryMap[realProductType],
      'NONE',
      cacheBurst
    ),
    PricingService.getPrices(market, channel, cacheBurst),
    SubscriptionService.getSubscriptions(market, channel, locale, cacheBurst),
    PromotionsService.getPromotions(market, channel, locale, cacheBurst),
    ContentService.getProductEnrichmentsDefinition(
      market,
      channel,
      locale,
      cacheBurst
    ),
    ECAPI.productCategories(market, channel, locale, cacheBurst),
    ECAPI.stocks(market, channel, cacheBurst)
  ]);

  const [productDtoMap, rangesMap, modelsToRanges] = createProductMaps(
    productsByCategories,
    productCategories
  );

  const singleProduct = productDtoMap.get(id);

  const stocks = new Map<string, boolean>(
    stocksArray.map((item) => [item.productId, item.isInStock])
  );

  if (singleProduct && (pricingMap.get(singleProduct.id)?.price ?? 0) >= 0) {
    const productI18nInstance = createI18nInstance<ProductTranslations>(
      defaultProductTranslations,
      enrichments.translations,
      locale
    );

    return fromEcapiDto(
      productByIdDto,
      pricingMap,
      subscriptions,
      rangesMap,
      promotions,
      enrichments,
      productCategories,
      modelsToRanges,
      stocks,
      productI18nInstance
    );
  }
  return null;
}
/* v8 ignore end */

export async function resolveLazyProducts(
  market: string,
  channel: string,
  locale: string,
  products: ProductBundle['contents']
): Promise<Map<string, BundleContentsProduct | null>> {
  const productsMap = new Map<string, BundleContentsProduct | null>();
  const resolvedProducts = products.filter((item) =>
    productBundleDataIsResolved(item)
  );
  resolvedProducts.forEach((product) => productsMap.set(product.id, product));

  const unresolvedProducts = products.filter((item) => productIsLazy(item));
  const promises = unresolvedProducts.map((productId) =>
    getProductById(productId.id, market, channel, locale, false)
  );
  const responses = await Promise.allSettled([...promises]);
  responses.map((response, index) =>
    productsMap.set(
      unresolvedProducts[index].id,
      response.status === 'fulfilled' && response.value
        ? {
            ...unresolvedProducts[index],
            ...(response.value as Product)
          }
        : null
    )
  );
  return productsMap;
}

export async function getProductsByIds(
  market: string,
  channel: string,
  locale: string,
  ids: string[],
  cacheBurst?: boolean
): Promise<Product[]> {
  const products = await Promise.all(
    ids.map((id) =>
      getProductById(id, market, channel, locale, cacheBurst).catch(() => null)
    )
  );

  return products.filter((product): product is Product => product !== null);
}
